/**
 * Centralised auth helpers for the Genesys Cloud PKCE flow.
 *
 * Storage-key names are kept compatible with the legacy implicit-grant flow so
 * downstream services keep reading the same sessionStorage entries. The token
 * and environment keys are sourced from src/constants.js (GC_TOKEN /
 * GC_ENVIRONMENT) so PrivateRoute, AdminRoute, Main and the services layer keep
 * working without changes.
 */

import config from '../../config';
import constants from '../../constants';

export const AuthenticationConstants = {
    CloudCSPToken: constants.GC_TOKEN,          // 'genesyscloud-token'
    CloudCSPEnv: constants.GC_ENVIRONMENT,      // 'genesyscloud-env'
    CodeVerifier: 'genesys_cloud_sdk_pkce_code_verifier',
    ExpireOn: 'expires_on',
    PostLoginPath: 'r2s-postlogin-path',
    UsePopup: 'r2s-use-popup'
};

export const AUTH_LOG_PREFIX = 'R2S --->';

// Popups validate by this exact string; keep it unique to this app.
export const POPUP_MESSAGE_TYPE = 'r2s-pop-auth';

/**
 * Resolve the OAuth client id for the active stage. Supports both `clientID`
 * (this app's config key) and `clientId` (the survey-wizard convention).
 */
export const getOAuthClientId = () => {
    if (!config) return null;
    return config.clientID || config.clientId || null;
};

/**
 * Best-effort check that sessionStorage is usable. Returns false in private
 * mode or when quota is exhausted (e.g. Safari ITP).
 */
export const isStorageAvailable = () => {
    try {
        const probeKey = '__r2s_storage_probe__';
        sessionStorage.setItem(probeKey, '1');
        sessionStorage.removeItem(probeKey);
        return true;
    } catch (err) {
        return false;
    }
};

export const getCloudEnv = () => {
    try {
        return sessionStorage.getItem(AuthenticationConstants.CloudCSPEnv) || null;
    } catch (err) {
        return null;
    }
};

export const setCloudEnv = (env) => {
    if (!env) return;
    try {
        sessionStorage.setItem(AuthenticationConstants.CloudCSPEnv, env);
    } catch (err) {
        console.error(AUTH_LOG_PREFIX, 'Failed to persist CloudCSPEnv', err);
    }
};

export const getAccessToken = () => {
    try {
        return sessionStorage.getItem(AuthenticationConstants.CloudCSPToken) || null;
    } catch (err) {
        return null;
    }
};

/**
 * True iff a token is present AND has not expired. Tokens without an expiry
 * record are treated as invalid so we never run on stale legacy implicit-grant
 * tokens after the upgrade.
 */
export const isTokenValid = () => {
    const token = getAccessToken();
    if (!token) return false;
    let expiresOnRaw = null;
    try {
        expiresOnRaw = sessionStorage.getItem(AuthenticationConstants.ExpireOn);
    } catch (err) {
        return false;
    }
    if (!expiresOnRaw) return false;
    const expiresOn = Number(expiresOnRaw);
    if (!Number.isFinite(expiresOn)) return false;
    return expiresOn > Date.now();
};

/**
 * Remove every auth-related sessionStorage entry. Safe to call multiple times.
 */
export const clearAuthSession = () => {
    try {
        sessionStorage.removeItem(AuthenticationConstants.CloudCSPToken);
        sessionStorage.removeItem(AuthenticationConstants.ExpireOn);
        sessionStorage.removeItem(AuthenticationConstants.CodeVerifier);
        sessionStorage.removeItem(AuthenticationConstants.PostLoginPath);
    } catch (err) {
        console.error(AUTH_LOG_PREFIX, 'Failed to clear auth session', err);
    }
};

export const setPostLoginPath = (path) => {
    if (!path) return;
    try {
        sessionStorage.setItem(AuthenticationConstants.PostLoginPath, path);
    } catch (err) {
        console.error(AUTH_LOG_PREFIX, 'Failed to persist post-login path', err);
    }
};

/**
 * One-shot read of the post-login path. The entry is removed after read so a
 * stale value never bleeds into a later login.
 */
export const consumePostLoginPath = () => {
    try {
        const path = sessionStorage.getItem(AuthenticationConstants.PostLoginPath);
        sessionStorage.removeItem(AuthenticationConstants.PostLoginPath);
        return path;
    } catch (err) {
        return null;
    }
};

export const isInIframe = () => {
    try {
        return window.self !== window.top;
    } catch (err) {
        // Cross-origin access throws on window.top in some browsers; that
        // itself is a strong signal we are inside a foreign frame.
        return true;
    }
};

/** Read the `usePopupAuth` query param (falsy unless explicitly truthy). */
const readPopupQueryParam = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        if (!params.has('usePopupAuth')) return null;
        const raw = (params.get('usePopupAuth') || '').toLowerCase().trim();
        if (raw === '' || raw === 'false' || raw === '0' || raw === 'no') return false;
        return true;
    } catch (err) {
        return null;
    }
};

/**
 * True when the AppFoundry integration URL has asked for popup auth, OR when
 * a previous page within this iframe already persisted that choice.
 */
export const shouldUsePopupAuth = () => {
    const fromQuery = readPopupQueryParam();
    if (fromQuery === true) {
        try {
            sessionStorage.setItem(AuthenticationConstants.UsePopup, 'true');
        } catch (err) { /* non-fatal */ }
        return true;
    }
    if (fromQuery === false) {
        try {
            sessionStorage.removeItem(AuthenticationConstants.UsePopup);
        } catch (err) { /* non-fatal */ }
        return false;
    }
    try {
        return sessionStorage.getItem(AuthenticationConstants.UsePopup) === 'true';
    } catch (err) {
        return false;
    }
};

/**
 * Parent-side helper: open the popup, wire up a single origin-validated
 * message listener, plus a watchdog that resolves with `{cancelled:true}` if
 * the user closes the popup without completing auth.
 *
 * Returns a Promise that resolves with:
 *  - `{accessToken, expireOn, env}` on success
 *  - `{blocked:true}` when the popup was blocked
 *  - `{cancelled:true}` on user dismissal or timeout
 *  - `{error}` when the popup posted back an error
 */
export const openAuthPopup = ({ width = 480, height = 620, timeoutMs = 60000 } = {}) => {
    return new Promise((resolve) => {
        let popup;
        try {
            const left = Math.max(0, Math.floor((window.screen.width - width) / 2));
            const top = Math.max(0, Math.floor((window.screen.height - height) / 2));
            popup = window.open(
                '/popuplogin',
                'r2s-popup-login',
                `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,` +
                `width=${width},height=${height},left=${left},top=${top}`
            );
        } catch (err) {
            console.error(AUTH_LOG_PREFIX, 'Failed to open popup', err);
            resolve({ blocked: true });
            return;
        }

        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
            resolve({ blocked: true });
            return;
        }

        let settled = false;
        const finish = (result) => {
            if (settled) return;
            settled = true;
            try { window.removeEventListener('message', onMessage); } catch (e) { /* noop */ }
            if (watchdog) clearInterval(watchdog);
            if (timeoutId) clearTimeout(timeoutId);
            resolve(result);
        };

        const onMessage = (event) => {
            // Strict origin validation - never trust `event.origin === '*'`.
            if (event.origin !== window.location.origin) return;
            const data = event && event.data;
            if (!data || data.type !== POPUP_MESSAGE_TYPE) return;
            if (data.error) {
                finish({ error: data.error });
                return;
            }
            if (!data.accessToken) return;
            try { popup.close(); } catch (e) { /* noop */ }
            finish({
                accessToken: data.accessToken,
                expireOn: data.expireOn,
                env: data.env
            });
        };
        window.addEventListener('message', onMessage, false);

        const watchdog = setInterval(() => {
            try {
                if (popup.closed) finish({ cancelled: true });
            } catch (err) {
                // Cross-origin access while the popup is on login.<env>.<host>
                // throws here; ignore until we get back to our origin.
            }
        }, 500);

        const timeoutId = setTimeout(() => {
            try { if (!popup.closed) popup.close(); } catch (e) { /* noop */ }
            finish({ cancelled: true });
        }, timeoutMs);
    });
};

export default {
    AuthenticationConstants,
    AUTH_LOG_PREFIX,
    POPUP_MESSAGE_TYPE,
    getOAuthClientId,
    isStorageAvailable,
    getCloudEnv,
    setCloudEnv,
    getAccessToken,
    isTokenValid,
    clearAuthSession,
    setPostLoginPath,
    consumePostLoginPath,
    isInIframe,
    shouldUsePopupAuth,
    openAuthPopup
};
